import {
  PERSON_TYPES,
  ADMIN_STATUS_PATHS,
  CUSTOM_ADMIN_STATUSES,
  ADMIN_STATUSES_BY_ROLE,
  ADMIN_ROLE_REPORT_COLUMN_MAPPING,
  ADMIN_STATUSES,
  NOT_ASSIGNED_FILTERS,
  DELIVERY_PROBLEM_LEVEL_OPTIONS,
  ADMIN_PROBLEM_FILTER_URL_PARAM,
  DELIVERY_PROBLEM_LEVEL_URL_PARAM_VALUES,
} from 'acces-impot-utils/lib/report'
import { ADMIN_ROLES } from 'acces-impot-utils/lib/user'
import { getApiClient } from '@/api/client'
import { getCurrentTaxYear } from '@/helpers/time'
import ASSIGNED_REPORTS_FRAGMENT from '@/api/fragments/assignedReportForList.gql'
import { ROUTE_NAMES } from '@/constants/route-names'
import { ADMIN_DETAILS_ROUTE_QUERY, ADMIN_DETAILS_SECTIONS } from './constants'
import { ACCI_FRONT_URL } from '@/constants/route-names'

export function topLevelTabs() {
  const isSuperUser = this.$store.getters['account/isSuperUser']
  const isManager = this.$store.getters['account/isManager']
  const statuses =
    ADMIN_STATUSES_BY_ROLE[this.accurateRole] ||
    ADMIN_STATUSES_BY_ROLE[ADMIN_ROLE_REPORT_COLUMN_MAPPING[this.accurateRole] || this.accurateRole]

  const fileStatuses = Object.values(statuses || {})

  if (isSuperUser) fileStatuses.unshift(CUSTOM_ADMIN_STATUSES.notAssigned)

  const isShowingDeliveryProblemLevels = isSuperUser || isManager

  return [
    {
      text: this.$t('myReports'),
      to: this.localePath({ name: ROUTE_NAMES.admin.index }),
      isActive: this.$route.name?.startsWith(ROUTE_NAMES.admin.index) && !this.$route.params.filter,
      hasChildrenVisible: () => true,
      children: fileStatuses.map(status =>
        getFileStatusTabConfig.call(this, status, undefined, isShowingDeliveryProblemLevels)
      ),
    },
    {
      text: this.$t('training'),
      externalLink: `${ACCI_FRONT_URL}/${this.$i18n.locale}/admin/training`,
    },
    ...(isSuperUser || this.accurateRole === ADMIN_ROLES.producer
      ? [
          {
            text: this.$t('remuneration'),
            to: this.localePath({ name: ROUTE_NAMES.admin.remuneration.index }),
          },
        ]
      : []),
  ]
}

export function reportTabs() {
  const { query: currentQuery } = this.$route
  const isProducer = this.$store.getters['account/isProducer']
  const isProducerOnProbation = this.$store.getters['account/isProducerOnProbation']
  const isSuperUser = this.$store.getters['account/isSuperUser']

  const client = getApiClient(this)
  const currentReportId = this.isAdminReportPage
    ? currentQuery[ADMIN_DETAILS_ROUTE_QUERY.id]
    : undefined
  const cachedReport = getCurrentReportFromCache(client, currentReportId)

  const getReportProp = getter => getter(cachedReport) || getter(this.currentReport)

  const getReportPath = (name, query = {}) =>
    this.localePath({
      name,
      query: {
        [ADMIN_DETAILS_ROUTE_QUERY.id]: currentQuery[ADMIN_DETAILS_ROUTE_QUERY.id],
        ...query,
      },
    })

  const personalDetailsByPersonKey = {}
  const reportPeople = getReportProp(r => r?.reportPeople) || []

  reportPeople.forEach(person => {
    personalDetailsByPersonKey[person.personKey] = {
      firstName: person.firstName,
      hasTaxReport: person.personType === PERSON_TYPES.self || !!person.hasTaxReport,
    }
  })

  const getPersonTabs = (person, section) => {
    const personalDetails = personalDetailsByPersonKey[person.personKey] || {}
    const query = {
      [ADMIN_DETAILS_ROUTE_QUERY.person]: person.personKey,
      [ADMIN_DETAILS_ROUTE_QUERY.focus]: section,
    }
    return {
      text: personalDetails.firstName || '-',
      to: getReportPath(ROUTE_NAMES.admin.report.details, query),
      isItalic: !personalDetails.hasTaxReport || false,
    }
  }
  const year = getReportProp(r => r?.year)

  return [
    {
      text: this.$t('reportMenu.reports', { year }),
      transformText: (text, $style = {}) => {
        return Number(year) < getCurrentTaxYear()
          ? text.replace(year, `<b class="${$style.textHighlighted}">${year}</b>`)
          : text
      },
      children: [
        {
          text: this.$t('reportMenu.general'),
          to: getReportPath(ROUTE_NAMES.admin.report.index),
        },
        ...(!isProducerOnProbation
          ? [
              {
                text: this.$t('reportMenu.processing'),
                to: getReportPath(ROUTE_NAMES.admin.report.processing),
              },
            ]
          : []),
        ...((isProducer && !isProducerOnProbation) || isSuperUser
          ? [
              {
                text: this.$t('reportMenu.emails'),
                to: getReportPath(ROUTE_NAMES.admin.report.emails),
              },
            ]
          : []),
        {
          text: this.$t('reportMenu.order'),
          to: getReportPath(ROUTE_NAMES.admin.report.order),
        },
      ],
    },
    {
      text: this.$t('reportMenu.people'),
      children: reportPeople.map(person => getPersonTabs(person, ADMIN_DETAILS_SECTIONS.people)),
    },
    {
      text: this.$t('reportMenu.answers'),
      children: (getReportProp(r => r?.reportAnswers) || []).map(person =>
        getPersonTabs(person, ADMIN_DETAILS_SECTIONS.answers)
      ),
    },
    {
      text: this.$t('reportMenu.documents'),
      children: (getReportProp(r => r?.reportDocuments) || []).map(person =>
        getPersonTabs(person, ADMIN_DETAILS_SECTIONS.documents)
      ),
    },
  ]
}

function getFileStatusTabConfig(status, query, isShowingDeliveryProblemLevels) {
  const tabConfig = {
    text: this.$tc(`adminStatuses.${status}`, 2),
    isActive:
      !this.isAdminTrainingPage &&
      !this.isAdminRemunerationPage &&
      ADMIN_STATUS_PATHS[status] === this.lastVisitedReportsPage?.params?.filter,
    to: this.localePath({
      name: ROUTE_NAMES.admin.reports,
      params: { filter: ADMIN_STATUS_PATHS[status] },
      query,
    }),
  }

  if (status === CUSTOM_ADMIN_STATUSES.notAssigned) {
    tabConfig.isChild = true
    tabConfig.isActiveOnCurrentRoute = true

    tabConfig.children = Object.values(NOT_ASSIGNED_FILTERS).map(filter => {
      return {
        text: this.$t(`adminStatuses.notAssignedFilters.${filter}`),
        to: this.localePath({
          name: ROUTE_NAMES.admin.reports.subFilter,
          params: {
            filter: ADMIN_STATUS_PATHS[status],
          },
          query: {
            ...query,
            [ADMIN_PROBLEM_FILTER_URL_PARAM]: Number(filter !== NOT_ASSIGNED_FILTERS.none),
          },
        }),
      }
    })
  }
  if (status === ADMIN_STATUSES.toSend && isShowingDeliveryProblemLevels) {
    tabConfig.isChild = true
    tabConfig.isActiveOnCurrentRoute = true

    tabConfig.children = Object.values(DELIVERY_PROBLEM_LEVEL_OPTIONS).map(level => {
      return {
        text: this.$t(`adminStatuses.deliveryProblemLevelOptions.${level}`),
        to: this.localePath({
          name: ROUTE_NAMES.admin.reports.subFilter,
          params: {
            filter: ADMIN_STATUS_PATHS[status],
          },
          query: {
            ...query,
            [ADMIN_PROBLEM_FILTER_URL_PARAM]: DELIVERY_PROBLEM_LEVEL_URL_PARAM_VALUES[level],
          },
        }),
      }
    })
  }

  return tabConfig
}

function getCurrentReportFromCache(client, id) {
  if (!id) return undefined

  try {
    const reportFragment = client?.readFragment({
      id: client.cache.identify({ __typename: 'Report', id }),
      fragment: ASSIGNED_REPORTS_FRAGMENT,
    })
    return reportFragment
  } catch (e) {
    return undefined
  }
}
